'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { apiFetch } from '@/lib/api'

interface Message {
  id: string
  senderRole: 'COMPANY' | 'PROVIDER'
  senderId: string
  content: string
  readAt: string | null
  createdAt: string
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}
function formatDay(iso: string) {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui"
  if (d.toDateString() === yesterday.toDateString()) return 'Hier'
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
}

const ROLE_LABEL: Record<string, string> = { COMPANY: 'Entreprise', PROVIDER: 'Prestataire' }

export function MissionChat({ missionId, myRole }: { missionId: string; myRole: 'COMPANY' | 'PROVIDER' }) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [mounted, setMounted] = useState(false)
  const hasAutoOpenedRef = useRef(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setMounted(true) }, [])

  const load = useCallback(async () => {
    try {
      const msgs = await apiFetch<Message[]>(`/missions/${missionId}/messages`)
      setMessages(msgs)
      const newUnread = msgs.filter((m) => m.senderRole !== myRole && !m.readAt).length
      setUnread((prev) => (open ? 0 : newUnread))
    } catch { /* silencieux */ }
  }, [missionId, myRole, open])

  useEffect(() => {
    load()
    const t = setInterval(load, 5000)
    return () => clearInterval(t)
  }, [load])

  // Auto-pop une seule fois à la première détection de messages non lus
  useEffect(() => {
    if (unread > 0 && !open && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true
      setOpen(true)
    }
  }, [unread, open])

  useEffect(() => {
    if (open && messages.length > 0) {
      setUnread(0)
      // Marquer les messages comme lus côté serveur
      apiFetch(`/missions/${missionId}/messages/read`, { method: 'PATCH' }).catch(() => {})
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
        inputRef.current?.focus()
      }, 80)
    }
  }, [open, messages.length, missionId])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const text = input.trim()
    if (!text) return
    setSending(true)
    setInput('')
    try {
      const msg = await apiFetch<Message>(`/missions/${missionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: text }),
      })
      setMessages((prev) => [...prev, msg])
    } catch { setInput(text) }
    finally { setSending(false) }
  }

  let lastDay = ''

  const panel = open && mounted ? createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        width: 340,
        height: 480,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 16,
        overflow: 'hidden',
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        boxShadow: '0 24px 64px rgba(0,0,0,0.20), 0 4px 16px rgba(0,0,0,0.10)',
        animation: 'chatSlideUp 0.30s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #F3F4F6', background: '#F9FAFB', flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(110,127,255,0.12)', border: '1px solid rgba(110,127,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="rgba(110,127,255,1)">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#111827', fontSize: 14, fontWeight: 600, margin: 0, lineHeight: 1.2 }}>Messages</p>
          <p style={{ color: '#9CA3AF', fontSize: 10, margin: 0 }}>
            {myRole === 'COMPANY' ? 'Avec le prestataire' : "Avec l'entreprise"}
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F3F4F6'; (e.currentTarget as HTMLButtonElement).style.color = '#374151' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#9CA3AF' }}
        >
          ✕
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px', background: '#fff' }}>
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, opacity: 0.4 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p style={{ color: '#9CA3AF', fontSize: 12, textAlign: 'center', margin: 0 }}>Aucun message.<br />Démarrez la conversation.</p>
          </div>
        )}

        {messages.map((m, i) => {
          const day = formatDay(m.createdAt)
          const showDay = day !== lastDay
          lastDay = day
          const mine = m.senderRole === myRole
          const prevMine = i > 0 && messages[i - 1].senderRole === m.senderRole
          const nextMine = i < messages.length - 1 && messages[i + 1].senderRole === m.senderRole
          const isFirst = !prevMine
          const isLast = !nextMine

          const borderRadius = mine
            ? isFirst && isLast ? '18px'
              : isFirst ? '18px 18px 6px 18px'
              : isLast ? '18px 6px 18px 18px'
              : '18px 6px 6px 18px'
            : isFirst && isLast ? '18px'
              : isFirst ? '18px 18px 18px 6px'
              : isLast ? '6px 18px 18px 18px'
              : '6px 18px 18px 6px'

          return (
            <div key={m.id}>
              {showDay && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
                  <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
                  <span style={{ fontSize: 10, color: '#9CA3AF', padding: '2px 8px', background: '#F9FAFB', borderRadius: 20 }}>{day}</span>
                  <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
                </div>
              )}

              {isFirst && !mine && (
                <p style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 4, marginBottom: 2, marginTop: 8 }}>{ROLE_LABEL[m.senderRole]}</p>
              )}

              <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginTop: prevMine ? 2 : 8 }}>
                <div style={{
                  maxWidth: '78%',
                  padding: '8px 14px',
                  fontSize: 14,
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                  background: mine ? '#6E7FFF' : '#F3F4F6',
                  color: mine ? '#FFFFFF' : '#111827',
                  borderRadius,
                }}>
                  {m.content}
                </div>
              </div>

              {isLast && (
                <p style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, textAlign: mine ? 'right' : 'left', paddingRight: mine ? 4 : 0, paddingLeft: mine ? 0 : 4 }}>
                  {formatTime(m.createdAt)}
                </p>
              )}
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>

      {/* Zone de saisie */}
      <form
        onSubmit={send}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderTop: '1px solid #F3F4F6', background: '#F9FAFB', flexShrink: 0 }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Écrire un message…"
          style={{ flex: 1, background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 24, padding: '8px 16px', fontSize: 14, color: '#111827', outline: 'none' }}
          disabled={sending}
          onFocus={(e) => { e.currentTarget.style.borderColor = '#6E7FFF' }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e as any) } }}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          style={{ width: 36, height: 36, borderRadius: '50%', background: '#6E7FFF', border: 'none', cursor: input.trim() && !sending ? 'pointer' : 'not-allowed', opacity: input.trim() && !sending ? 1 : 0.35, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'opacity 0.15s, transform 0.1s' }}
          onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.92)' }}
          onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </form>

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>,
    document.body
  ) : null

  return (
    <>
      {/* Bouton toggle inline dans la mission card */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl relative transition-all duration-200',
          open
            ? 'bg-accent text-white shadow-lg shadow-accent/30'
            : unread > 0
              ? 'bg-accent/20 border border-accent/50 text-accent'
              : 'glass-btn',
        ].join(' ')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Messages
        {unread > 0 && !open && (
          <span className="ml-1 min-w-[20px] h-5 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {panel}
    </>
  )
}
